import type React from "react";
import { cn } from "../../lib/utils";

/**
 * A section heading set as display type: heavy capitals, tight lines, sized
 * for a 375px phone first. The theme still picks the face and weight; this only
 * sets case, size and rhythm, so the same heading reads right in any theme.
 * Shared by every section's `bold` style so their headings match.
 */
const DISPLAY_HEADING_CLASS =
    "jobsite:font-heading jobsite:uppercase jobsite:text-[2rem] jobsite:leading-none jobsite:tracking-[-0.01em] jobsite:text-balance jobsite:break-words jobsite:sm:text-[2.5rem] jobsite:md:text-5xl",

  /** The short accent rule above a display heading: colour on a page that may have no photos. */
  DISPLAY_RULE_CLASS = "jobsite:mb-5 jobsite:block jobsite:h-1.5 jobsite:w-12 jobsite:bg-accent",

  /**
   * A section's heading and lede in the bold style.
   * @param {object} props The heading block.
   * @param {React.ReactNode} props.heading The heading; the editors may pass an inline-editable element.
   * @param {React.ReactNode} [props.subheading] The lede under it, if any.
   * @param {boolean} [props.onDark] Whether it sits on a dark band (`bg-primary`).
   * @param {boolean} [props.centered] Centre it, for layouts that centre their content.
   * @param {string} [props.className] Extra classes for the wrapper.
   * @returns {React.JSX.Element} The heading block.
   */
  DisplayHeading = ({
    heading,
    subheading,
    onDark = false,
    centered = false,
    className,
  }: {
    heading: React.ReactNode;
    subheading?: React.ReactNode;
    onDark?: boolean;
    centered?: boolean;
    className?: string;
  }): React.JSX.Element => {
    const hasSubheading = subheading !== undefined && subheading !== null && subheading !== "";
    return (
      <div className={cn("jobsite:mb-10 jobsite:max-w-3xl jobsite:md:mb-12", centered && "jobsite:mx-auto jobsite:text-center", className)}>
        <span aria-hidden="true" className={cn(DISPLAY_RULE_CLASS, centered && "jobsite:mx-auto")} />
        <h2 className={cn(DISPLAY_HEADING_CLASS, onDark ? "jobsite:text-primary-foreground" : "jobsite:text-foreground")}>
          {heading}
        </h2>
        {hasSubheading && (
          <p
            className={cn(
              "jobsite:mt-4 jobsite:text-base jobsite:leading-relaxed jobsite:text-pretty jobsite:sm:text-lg",
              onDark ? "jobsite:text-primary-foreground/80" : "jobsite:text-muted-foreground",
            )}
          >
            {subheading}
          </p>
        )}
      </div>
    );
  };

export { DISPLAY_HEADING_CLASS, DISPLAY_RULE_CLASS, DisplayHeading };
